/**
 * One workspace of an organization — and the shell its rows share.
 *
 * Entering a workspace replaces the rail, so this is not a page with tabs: it
 * is a **place**, and Projects, Access, Integrations, Secrets and History are
 * rows in it. Workflows are not one of them: they belong to the organization,
 * on its own rail, because a chain of instructions is the same chain whichever
 * workspace runs it. What they have in common is held here — the document
 * that was read, the draft being edited, and the one verb that commits it —
 * because a draft that lived in each section would be six drafts, and moving
 * between two rows of the same workspace would quietly drop one of them.
 *
 * A save is a **publish**: a new version every member's install pulls, recorded
 * with who made it and a diff. It carries the version the page was opened on
 * (`If-Match`), so somebody else's publish in the meantime is a 409 with what
 * arrived, never a silent overwrite; and it carries the secrets typed below in
 * the same request, so the document and the values it names land together.
 *
 * The server holds the document to exactly what the app would keep and answers
 * a refusal with every problem by path; each section shows the ones about it.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import type React from 'react';
import { rightAllows } from '@airlock/contracts/cloud/wire';
import { groupChanges, looksOf } from '@airlock/contracts/workspace/change-groups';
import { withoutPersonal } from '@airlock/contracts/workspace/personal';
import { secretPathsOf } from '@airlock/contracts/workspace/secrets';
import { color } from '../../theme/tokens';
import { HeaderButton } from '../../components/Shell';
import { ListEmpty, Note, PageBar, PageBody, PageLede } from '../../components/list';
import { PublishReceipt } from '../../components/PublishReceipt';
import { Card, Empty, Field, GhostButton, Input, RowAction } from '../../components/primitives';
import { useConfirm } from '../../components/Modal';
import { api, ifMatch, apiPath } from '../../lib/api';
import type {
  DocConfig,
  DocProject,
  Me,
  OrgSettingsRecord,
  WorkspaceDocument,
  WorkspaceRecord,
  PublishConflict,
  PublishResponse,
  SecretWrites
} from '../../lib/types';
import { FolderScan } from './FolderScan';
import { ProjectEditor, newRepo } from './ProjectEditor';
import { planPublish, problemInWords, teamDiff } from './publish-plan';
import { ChangeRows, PublishReview } from './publish-review';
import { SecretsField } from './SecretsField';
import { ALL_AGENTS, SECTION_SPECS } from './org-catalogue';
import { WorkspaceRulesRepo } from './rules/workspace-rules-repo';
import { WORKSPACE_INTEGRATION_SPEC, WorkspaceSettings, type OrgLayer } from './workspace-settings';
import {
  ColorPicker,
  JsonField,
  ProblemList,
  actorLabel,
  administers,
  conflictOf,
  emptyDocument,
  failureText,
  problemsUnder,
  refusalProblems,
  stamp
} from './shared';

/** Which row of the workspace's rail is being drawn. */
export type WorkspaceSection = 'projects' | 'integrations' | 'secrets' | 'settings' | 'agents';

/**
 * The agents page a workspace has: `ALL_AGENTS` minus the row that is not a
 * workspace's to answer. Which agents a member may launch is handed to an
 * install once, from the organization — a workspace overrides how an agent
 * *runs*, never which ones exist.
 */
const WORKSPACE_AGENTS = ALL_AGENTS;

export function WorkspacePage({
  me,
  orgId,
  orgName,
  workspaceId,
  section,
  pane,
  onName,
  onLeave,
  onCreated,
  onBrowseRules
}: {
  me: Me;
  orgId: string;
  /** The organization's own name, which the settings rows say out loud. */
  orgName: string;
  /** `null` while a workspace is being started rather than opened. */
  workspaceId: string | null;
  section: WorkspaceSection;
  /** Which compartment of Settings or Agents the rail has open. */
  pane: string | null;
  /** The breadcrumb needs the name, and only this page has read it. */
  onName: (name: string | null) => void;
  onLeave: () => void;
  onCreated: (workspaceId: string) => void;
  /** To this workspace's Rules page. */
  onBrowseRules: () => void;
}): React.JSX.Element {
  const [record, setRecord] = useState<WorkspaceRecord | null>(null);
  const [draft, setDraft] = useState<WorkspaceDocument>(emptyDocument);
  // Bumped on every load, so the JSON fields drop what was typed into them and
  // read the document again.
  const [generation, setGeneration] = useState(0);
  const [invalid, setInvalid] = useState<Record<string, boolean>>({});
  const [writes, setWrites] = useState<SecretWrites>({});
  // Which projects' cards are unfolded (§46.2): a project added on this page
  // opens on arrival, the published ones start folded.
  const [unfolded, setUnfolded] = useState<ReadonlySet<string>>(new Set());
  const unfold = (ids: string[]): void => setUnfolded((current) => new Set([...current, ...ids]));
  const [problems, setProblems] = useState<string[]>([]);
  const [dialog, ask] = useConfirm();
  // A publish of an existing workspace goes through its review page first (kit
  // 73), and what is left unticked there stays in the draft between publishes.
  const [reviewing, setReviewing] = useState(false);
  const [held, setHeld] = useState<ReadonlySet<string>>(new Set());
  const [conflict, setConflict] = useState<PublishConflict | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(workspaceId !== null);
  // The organization's layer, read on its own: it is a different document with
  // a version of its own, every member may read it, and the settings rows
  // cannot say what is applying — or what has stopped applying — without it.
  const [org, setOrg] = useState<OrgLayer | null>(null);
  const admin = administers(me, orgId);

  /** Take their version, losing what is on this page. Asked first, because
   *  nothing on screen has been published and there is no undo for it. */
  const reloadTheirs = async (): Promise<void> => {
    const agreed = await ask({
      title: 'Reload their version?',
      lede: 'Your edits on this page are dropped, and theirs is what you carry on from.',
      confirm: 'Reload theirs',
      danger: true
    });
    if (agreed) void load();
  };

  const load = useCallback(async (): Promise<void> => {
    setProblems([]);
    setConflict(null);
    setWrites({});
    setInvalid({});
    setHeld(new Set());
    setReviewing(false);
    if (!workspaceId) {
      setRecord(null);
      setDraft(withoutPersonal(emptyDocument()));
      setGeneration((current) => current + 1);
      return;
    }
    setLoading(true);
    try {
      const answer = await api.get<WorkspaceRecord>(apiPath`/orgs/${orgId}/workspaces/${workspaceId}`);
      setRecord(answer);
      setDraft(withoutPersonal(answer.document));
      setGeneration((current) => current + 1);
    } finally {
      setLoading(false);
    }
  }, [orgId, workspaceId]);

  useEffect(() => {
    load().catch((failure: unknown) => setError(failureText(failure, 'That workspace could not be read.')));
  }, [load]);

  // A failure here is silent on purpose: the rows still draw, showing the
  // application's own values, and a workspace whose organization's settings
  // could not be read is not a workspace that cannot be edited.
  useEffect(() => {
    let live = true;
    void api
      .get<OrgSettingsRecord>(apiPath`/orgs/${orgId}/settings`)
      .then((answer) => {
        if (!live) return;
        setOrg({
          name: orgName,
          version: answer.version,
          defaults: answer.settings.defaults as Record<string, unknown>,
          enforced: answer.settings.enforced
        });
      })
      .catch(() => setOrg(null));
    return () => {
      live = false;
    };
  }, [orgId, orgName]);

  // The rail's breadcrumb says which workspace is open, and the name only
  // arrives with the document — so it is handed up as soon as it is known,
  // and taken back when this page goes away.
  useEffect(() => {
    onName(record ? record.workspace.name : workspaceId ? null : 'New workspace');
    return () => onName(null);
  }, [record, workspaceId, onName]);

  // The rail moves away from a review the way it moves away from any page.
  useEffect(() => setReviewing(false), [section, pane]);

  const plan = useMemo(
    () =>
      record
        ? planPublish({
            published: record.document,
            draft,
            writes,
            heldSecrets: new Set(record.secrets.map((secret) => secret.path)),
            held
          })
        : null,
    [record, draft, writes, held]
  );

  const toggle = (paths: string[], on: boolean): void =>
    setHeld((current) => {
      const next = new Set(current);
      for (const path of paths) {
        if (on) next.delete(path);
        else next.add(path);
      }
      return next;
    });

  const mark =
    (field: string) =>
    (valid: boolean): void =>
      setInvalid((current) => ({ ...current, [field]: !valid }));

  /** JSON that does not parse has no diff to read, here or at the Worker. */
  const parses = (): boolean => {
    if (!Object.values(invalid).some(Boolean)) return true;
    setError('Some JSON on this workspace does not parse yet. Fix it before publishing.');
    return false;
  };

  const save = async (message = ''): Promise<void> => {
    if (!parses()) return;
    setBusy(true);
    setError(null);
    setNote(null);
    setProblems([]);
    setConflict(null);
    // A line only when the review page's foot was given one: a first version has
    // no such page, and the history records who and when whatever it says.
    const document = plan?.document ?? withoutPersonal(draft);
    const secrets =
      plan?.secrets ?? Object.fromEntries(Object.entries(writes).filter(([path]) => secretPathsOf(document).has(path)));
    const body = {
      document,
      ...(Object.keys(secrets).length ? { secrets } : {}),
      ...(message ? { message } : {})
    };
    try {
      if (!workspaceId || !record) {
        const created = await api.post<PublishResponse>(apiPath`/orgs/${orgId}/workspaces`, body);
        setNote(`Created ${created.workspace.name}. Members see it at their next check.`);
        onCreated(created.workspace.id);
      } else {
        const published = await api.send<PublishResponse>(
          'POST',
          apiPath`/orgs/${orgId}/workspaces/${workspaceId}/publish`,
          body,
          ifMatch(record.workspace.version)
        );
        const going = plan?.going.length ?? 0;
        const waiting = (plan?.lines.length ?? 0) - going;
        if (plan?.partial) {
          // The draft stays as it is: what was left unticked is still in it, and
          // the record it now differs from is the version just written.
          setRecord(await api.get<WorkspaceRecord>(apiPath`/orgs/${orgId}/workspaces/${workspaceId}`));
          setWrites(plan.left);
          setReviewing(false);
        } else await load();
        setNote(
          `Published version ${published.workspace.version} — ${going} change${going === 1 ? '' : 's'}${waiting ? ` · ${waiting} still in your draft` : ''}.`
        );
      }
    } catch (failure) {
      const refused = refusalProblems(failure);
      const lost = conflictOf<PublishConflict>(failure);
      if (refused) setProblems(refused);
      else if (lost) setConflict(lost);
      else setError(failureText(failure));
    } finally {
      setBusy(false);
    }
  };

  const archive = async (): Promise<void> => {
    if (!workspaceId || !record) return;
    const agreed = await ask({
      title: `Archive ${record.workspace.name}?`,
      lede: 'Members stop pulling it and their workspaces stop being copies of it — nothing on their machines is deleted.',
      confirm: 'Archive it',
      danger: true
    });
    if (!agreed) return;
    try {
      await api.del(apiPath`/orgs/${orgId}/workspaces/${workspaceId}`);
      await load();
    } catch (failure) {
      setError(failureText(failure));
    }
  };

  if (loading && !record) return <ListEmpty title="Reading the workspace…" />;
  if (workspaceId && !record) {
    return (
      <PageBody>
        <Empty title="That workspace could not be read">
          {error}
          <div style={{ marginTop: 18 }}>
            <GhostButton onClick={onLeave}>All workspaces</GhostButton>
          </div>
        </Empty>
      </PageBody>
    );
  }

  const setProject = (index: number, next: DocProject): void =>
    setDraft((current) => ({ ...current, repos: current.repos.map((repo, at) => (at === index ? next : repo)) }));
  const claimed = new Set<string>();
  const under = (prefix: string): string[] => {
    const found = problemsUnder(problems, prefix);
    found.forEach((problem) => claimed.add(problem));
    return found;
  };
  const sections = {
    head: [...under('name'), ...under('color'), ...under('schema')],
    projects: draft.repos.map((repo, index) => [...under(`repos[${repo.name}]`), ...under(`repos[${index}]`)]),
    config: under('config'),
    taskSources: under('taskSources')
  };
  const unclaimed = problems.filter((problem) => !claimed.has(problem));
  const archived = Boolean(record?.project.archivedAt);
  const next = record ? record.workspace.version + 1 : 1;
  /**
   * Whether this page is an editor or a reading.
   *
   * A read grant is a real grant — `read · Platform` is how a workspace is
   * handed to a team that only pulls it — and this page was drawn the
   * same for it: every field live, `Publish` offered, and the refusal arriving
   * from the Worker at the end of it, after the typing. The right travels on
   * the record the page has already read, so nothing more is asked for.
   *
   * It is `rightAllows` and not `=== 'write'`, which is the whole of a bug this
   * line shipped with: the Worker hands an owner, an administrator and anybody
   * holding a workspace `admin` grant the right `admin` (`standingOn`), so the
   * equality made **the administrators** the read-only ones — every field
   * disabled and no `Publish` — on a document they are the only people allowed
   * to publish. The rights are a ladder, so the question is "is this at least
   * write", never "is this exactly write".
   *
   * A workspace being started has no record and no grant to read: whether an
   * account may create one is the create route's answer, not this line's.
   */
  const mayWrite = record ? rightAllows(record.workspace.access.right, 'write') : true;
  /**
   * Archiving and restoring, which the grant decides and not the role.
   *
   * It was `administers(me, orgId)` — the org role alone — and that is not who
   * the Worker accepts: `requireRight(…, 'admin')` takes a workspace's own
   * `admin` grant as well, which is what the Access page promises when it
   * offers *Publish it, archive it, restore it, and decide this page*. A member
   * given that grant had the right and no button.
   */
  const mayAdminister = record ? rightAllows(record.workspace.access.right, 'admin') : admin;
  // Which catalogue the rows are drawn against, and which compartment of it.
  // The rail is what names the compartment, and a name it does not have falls
  // back to the first — the same answer `firstSection` gives.
  const settingsSpec =
    section === 'agents' ? WORKSPACE_AGENTS : (SECTION_SPECS[pane ?? ''] ?? SECTION_SPECS.sessions);
  const writeConfig = (patch: Partial<DocConfig>): void =>
    setDraft((current) => ({ ...current, config: { ...current.config, ...patch } }));

  return (
    <>
      {dialog}
      <PageBar>
        {/* Archive on the left, the verb that commits on the right (rule 6). */}
        {reviewing ? (
          <HeaderButton disabled={busy} onClick={() => setReviewing(false)}>
            Back to editing
          </HeaderButton>
        ) : (
          record && mayAdminister && !archived && <HeaderButton onClick={() => void archive()}>Archive</HeaderButton>
        )}
        <span style={{ flex: 1 }} />
        {/* Discard just left of Publish while the draft differs (redesign §13). */}
        {mayWrite && !archived && !busy && !reviewing && record && plan && plan.lines.length > 0 && (
          <HeaderButton
            onClick={() => {
              setDraft(withoutPersonal(record.document));
              setWrites({});
              setHeld(new Set());
              setGeneration((current) => current + 1);
            }}
          >
            Discard
          </HeaderButton>
        )}
        {/* An archived workspace is read, never published (redesign, 104h). On the
            review its verb stands in the foot, with what it does (kit 74, Q3). */}
        {mayWrite && !archived && !reviewing && (
          <HeaderButton
            primary
            onClick={() => {
              if (!record) void save();
              else if (parses()) {
                setError(null);
                setNote(null);
                setReviewing(true);
              }
            }}
            disabled={busy}
            // Held while the draft is what is published (proposal 27).
            reason={busy || !plan ? undefined : !plan.lines.length ? 'Nothing to publish yet' : undefined}
          >
            {busy ? (record ? 'Publishing' : 'Creating') : record ? `Publish v${next}` : 'Create the workspace'}
          </HeaderButton>
        )}
      </PageBar>

      {!mayWrite && (
        <div style={{ padding: '16px var(--al-gutter) 0' }}>
          <Note>
            You have <strong style={{ fontWeight: 600 }}>read</strong> access to this workspace
            {record?.workspace.access.via ? ` — ${record.workspace.access.via}` : ''}. Everything it publishes is here to
            read; changing it is for somebody with write access, so this page holds no controls for you.
          </Note>
        </div>
      )}

      {archived && (
        <div style={{ padding: '16px var(--al-gutter) 0' }}>
          <Note tone="warn">
            Archived. Members no longer pull this workspace, and their copies became workspaces of their own when it was
            archived.
          </Note>
        </div>
      )}

      {conflict && (
        <div style={{ padding: '16px var(--al-gutter) 0' }}>
          <Note tone="warn">
            <strong style={{ fontWeight: 600 }}>Somebody published since you opened this.</strong> Version{' '}
            {conflict.current.version} was published {stamp(conflict.current.updatedAt)} by{' '}
            {actorLabel(conflict.current.updatedBy)}. What it changed since the version you are editing:
            <div style={{ marginTop: 12, background: color.surface, borderRadius: 8, padding: 12 }}>
              <ChangeRows groups={groupChanges(teamDiff(conflict.diff), looksOf(draft.repos))} />
            </div>
            <div style={{ display: 'flex', gap: 14, marginTop: 12 }}>
              <RowAction
                onClick={() => void reloadTheirs()}
              >
                Reload their version
              </RowAction>
              <RowAction onClick={() => setConflict(null)}>Keep editing</RowAction>
            </div>
          </Note>
        </div>
      )}

      {!reviewing && unclaimed.length > 0 && (
        <div style={{ padding: '16px var(--al-gutter) 0' }}>
          <ProblemList problems={unclaimed} />
        </div>
      )}
      {note && (
        <div style={{ padding: '16px var(--al-gutter) 0' }}>
          <PublishReceipt text={note} />
        </div>
      )}

      {reviewing && record && plan ? (
        <PublishReview
          version={record.workspace.version}
          plan={plan}
          problems={problems.map((problem) => problemInWords(problem, draft))}
          busy={busy}
          onToggle={toggle}
          onPublish={(message) => void save(message)}
        />
      ) : (
      <PageBody>
        {/* One `fieldset` rather than a `readOnly` threaded through five
            editors: a read grant is the state of the whole page, the browser
            already knows how to draw a control nobody may touch, and a prop
            passed down four levels of editor is four places for it to be
            forgotten on the next one. */}
        <fieldset
          disabled={!mayWrite}
          style={{ display: 'flex', flexDirection: 'column', gap: 18, border: 0, margin: 0, padding: 0, minWidth: 0 }}
        >
          {section === 'projects' && (
            <>
              <PageLede>
                What a member who joins clones, and how each one installs, starts and checks. Every project needs a remote
                they can reach — the folder each lands in is theirs to pick.
              </PageLede>
              <Card padding={20}>
                <div className="al-two" style={{ display: 'grid', gridTemplateColumns: 'minmax(0,2fr) minmax(0,1fr)', gap: 14 }}>
                  <Field label="Workspace name" htmlFor="workspace-name">
                    <Input
                      id="workspace-name"
                      value={draft.name}
                      placeholder="Acme platform"
                      onChange={(event) => setDraft((current) => ({ ...current, name: event.target.value }))}
                    />
                  </Field>
                  <Field label="Color">
                    <ColorPicker
                      value={draft.color}
                      label="Color of this workspace"
                      disabled={!mayWrite}
                      onChange={(next) => setDraft((current) => ({ ...current, color: next }))}
                    />
                  </Field>
                </div>
                <ProblemList problems={sections.head} />
              </Card>

              {/* The two ways a project is added, and the folder comes first:
                  it fills fourteen fields the other one leaves empty, and the
                  repository is usually already on the machine of whoever is
                  publishing it. Both are offered — a workspace is published from
                  a browser that may not be near the code at all. */}
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 17, fontWeight: 600, color: color.ink }}>Projects</span>
                <span style={{ flex: 1 }} />
                {draft.repos.length >= 2 && (
                  <RowAction
                    onClick={() =>
                      setUnfolded(unfolded.size >= draft.repos.length ? new Set() : new Set(draft.repos.map((repo) => repo.id)))
                    }
                  >
                    {unfolded.size >= draft.repos.length ? 'Collapse all' : 'Expand all'}
                  </RowAction>
                )}
                {mayWrite && (
                  <FolderScan
                    nextOrder={draft.repos.length}
                    existing={draft.repos.map((repo) => repo.remote)}
                    onRepos={(repos) => {
                      unfold(repos.map((repo) => repo.id));
                      setDraft((current) => ({ ...current, repos: [...current.repos, ...repos] }));
                    }}
                  />
                )}
                <GhostButton
                  size="sm"
                  onClick={() => {
                    const added = newRepo(draft.repos.length);
                    unfold([added.id]);
                    setDraft((current) => ({ ...current, repos: [...current.repos, added] }));
                  }}
                >
                  Add a project
                </GhostButton>
              </div>
              {draft.repos.length === 0 && (
                <Empty title="No projects yet">
                  A member who joins clones each one under a folder they pick, so every project needs a remote they can
                  reach. Point at a folder on this computer and every field a repository answers for is filled in.
                </Empty>
              )}
              <div style={{ borderBottom: draft.repos.length ? `1px solid ${color.line}` : undefined }}>
                {draft.repos.map((repo, index) => (
                  <ProjectEditor
                    key={`${repo.id}-${generation}`}
                    repo={repo}
                    problems={sections.projects[index] ?? []}
                    onChange={(nextRepo) => setProject(index, nextRepo)}
                    onRemove={() =>
                      setDraft((current) => ({ ...current, repos: current.repos.filter((_, at) => at !== index) }))
                    }
                    presence={record?.secrets ?? []}
                    writes={writes}
                    onWrites={setWrites}
                    endToEnd={record?.sealing === 'e2e'}
                    open={unfolded.has(repo.id)}
                    onToggle={() =>
                      setUnfolded((current) => {
                        const next = new Set(current);
                        if (next.has(repo.id)) next.delete(repo.id);
                        else next.add(repo.id);
                        return next;
                      })
                    }
                    draft={draftOf(record?.document.repos, repo)}
                  />
                ))}
              </div>
              <WorkspaceRulesRepo
                value={draft.config.rulesRepository}
                projects={draft.repos}
                onChange={(rulesRepository) => writeConfig({ rulesRepository })}
                onBrowse={onBrowseRules}
              />
            </>
          )}

          {section === 'secrets' && (
            <>
              <PageLede>
                {record?.sealing === 'e2e'
                  ? 'A value is sealed end to end: in Airlock, on a member’s machine, under a key only your machines hold. This page can say that one exists, who last set it and when, and remove it — it cannot set one or read one back.'
                  : 'A value is sealed under this organization’s own key and opened only for a linked machine. This page can say that one exists, who last set it and when — it can never read one back.'}
              </PageLede>
              <Card padding={20}>
                <SecretsField
                  document={draft}
                  presence={record?.secrets ?? []}
                  writes={writes}
                  onWrites={setWrites}
                  endToEnd={record?.sealing === 'e2e'}
                />
              </Card>
            </>
          )}

          {/* Settings and Agents are the same rows drawn against two catalogues,
              which is the whole of the difference between them: one opens on the
              compartment the rail names, the other has a single page. */}
          {(section === 'settings' || section === 'agents') && (
            <>
              <PageLede>{settingsSpec.lede}</PageLede>
              {settingsSpec.notes?.map((text) => (
                <Note key={text}>{text}</Note>
              ))}
              <WorkspaceSettings
                spec={settingsSpec}
                config={draft.config}
                org={org}
                editable={mayWrite ? writeConfig : undefined}
                repos={draft.repos}
              />
              <ProblemList problems={sections.config} />
              <p style={{ margin: 0, maxWidth: 640, fontSize: 12, lineHeight: 1.6, color: color.inkFaint }}>
                A row left inherited does not enter the document at all: each member’s install keeps its own value for it.
                The three lists a workspace widens rather than replaces — the egress allowlist, the blocklist and the host
                services — are not rows and are edited in Airlock itself.
              </p>
            </>
          )}

          {section === 'integrations' && (
            <>
              <PageLede>
                The organization connects an account once; a workspace names what it uses. Which Jira or Trello boards feed
                this one — each member signs in to the tracker with their own credential.
              </PageLede>
              <WorkspaceSettings
              spec={WORKSPACE_INTEGRATION_SPEC}
              config={draft.config}
              org={org}
              editable={mayWrite ? writeConfig : undefined}
            />
              <Card padding={20}>
                <JsonField
                  key={`sources-${generation}`}
                  id="workspace-sources"
                  label="Ticket boards"
                  rows={8}
                  value={draft.taskSources}
                  onValue={(value) => setDraft((current) => ({ ...current, taskSources: value as WorkspaceDocument['taskSources'] }))}
                  onValidity={mark('taskSources')}
                  hint="Each entry names a host, a board key, and which of its statuses map onto the lanes."
                />
                <ProblemList problems={sections.taskSources} />
              </Card>
            </>
          )}
        </fieldset>
      </PageBody>
      )}

      {/* What a publish refused, at the foot of whichever row is open. The
          paragraph that stood here explained what publishing does to every
          member's machine, over a button that says Publish; what is left is the
          two things that are *news* — a refusal, here, and a receipt, which
          since #121 stands at the head of the content (PublishReceipt). */}
      <div style={{ padding: '18px var(--al-gutter) 30px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 640 }}>
          {error && <p style={{ fontSize: 13, color: color.badText, margin: 0 }}>{error}</p>}
        </div>
      </div>
    </>
  );
}

/** How a project in the draft differs from the published document (§46.2's DRAFT label). */
function draftOf(published: readonly DocProject[] | undefined, repo: DocProject): 'new' | 'edited' | undefined {
  if (!published) return undefined;
  const before = published.find((one) => one.id === repo.id);
  if (!before) return 'new';
  return JSON.stringify(before) === JSON.stringify(repo) ? undefined : 'edited';
}
